import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import type { ComponentPropsWithRef, InputHTMLAttributes, SelectHTMLAttributes } from 'react';

const control =
  'block w-full rounded-lg border border-line bg-surface px-3 text-text placeholder:text-muted/70 shadow-card transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25 aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/20 disabled:opacity-60';

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  /** Shown at the right of the label row, e.g. a character counter. */
  aside?: ReactNode;
  /** A single control; it receives id, aria-invalid and aria-describedby. */
  children: ReactElement<Record<string, unknown>>;
  className?: string;
}

/** Label + control + hint/error, wired together for screen readers. */
export function Field({ label, hint, error, required, aside, children, className = '' }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(' ') || undefined;
  const control = isValidElement(children)
    ? cloneElement(children, { id, 'aria-invalid': !!error, 'aria-describedby': describedBy, required })
    : children;

  return (
    <div className={className}>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="block text-sm font-medium">
          {label}
          {required && (
            <span className="text-danger" aria-hidden>
              {' '}
              *
            </span>
          )}
        </label>
        {aside}
      </div>
      {control}
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-sm text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1.5 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`${control} h-10 ${className}`} {...props} />;
}

export function Textarea({ className = '', ...props }: ComponentPropsWithRef<'textarea'>) {
  return <textarea className={`${control} min-h-28 py-2.5 ${className}`} {...props} />;
}

type SelectOption = string | { value: string; label: string };

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Plain options, or groups shown under a heading. */
  options: readonly (SelectOption | { group: string; options: readonly SelectOption[] })[];
  placeholder?: string;
}

function renderOption(o: SelectOption) {
  const { value, label } = typeof o === 'string' ? { value: o, label: o } : o;
  return (
    <option key={value} value={value}>
      {label}
    </option>
  );
}

export function Select({ options, placeholder, className = '', ...props }: SelectProps) {
  return (
    <select className={`${control} h-10 pr-8 ${className}`} {...props}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) =>
        typeof o === 'object' && 'group' in o ? (
          <optgroup key={o.group} label={o.group}>
            {o.options.map(renderOption)}
          </optgroup>
        ) : (
          renderOption(o)
        ),
      )}
    </select>
  );
}

/** Radio options shown as tappable cards. */
export function ChoiceCards({
  name,
  options,
  value,
  onChange,
  error,
  label,
  required = true,
}: {
  name: string;
  label: string;
  options: { value: string; label: string; description?: string }[];
  value: string;
  onChange: (v: string) => void;
  error?: string;
  required?: boolean;
}) {
  const id = useId();
  return (
    <fieldset aria-describedby={error ? `${id}-error` : undefined}>
      <legend className="mb-1.5 text-sm font-medium">
        {label}
        {required && (
          <span className="text-danger" aria-hidden>
            {' '}
            *
          </span>
        )}
      </legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((o) => (
          <label
            key={o.value}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 shadow-card transition-colors ${
              value === o.value ? 'border-brand bg-brand-soft' : 'border-line bg-surface hover:bg-surface-2'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              className="mt-1 accent-(--brand)"
            />
            <span>
              <span className="block font-medium">{o.label}</span>
              {o.description && <span className="block text-sm text-muted">{o.description}</span>}
            </span>
          </label>
        ))}
      </div>
      {error && (
        <p id={`${id}-error`} className="mt-1.5 text-sm text-danger">
          {error}
        </p>
      )}
    </fieldset>
  );
}
